import { useState } from 'react';
import { VideoCameraIcon, ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Spinner from '../../common/Spinner';

/**
 * TelehealthRoom — embeds a Daily.co video call or opens an external link.
 *
 * Props:
 *   url       — the join URL returned by the backend (Daily.co prebuilt URL with token, or raw link)
 *   onClose   — called when the user dismisses the room
 */
export default function TelehealthRoom({ url, onClose }) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [useExternalFallback, setUseExternalFallback] = useState(false);

  // Daily.co prebuilt UI URLs look like https://subdomain.daily.co/room?t=token
  // External links (Zoom, etc.) should open in a new tab
  const isDaily = url && url.includes('.daily.co/');

  if (!url) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <VideoCameraIcon className="h-12 w-12 text-neutral-300" />
        <p className="text-neutral-500 text-sm">No telehealth link available for this appointment.</p>
        <button onClick={onClose} className="btn btn-secondary text-sm">Close</button>
      </div>
    );
  }

  if (!isDaily || useExternalFallback) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <VideoCameraIcon className="h-12 w-12 text-primary-400" />
        <p className="text-neutral-700 text-sm text-center max-w-sm">
          Click below to join the video call. The session will open in a new tab.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary flex items-center gap-2"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          Join video call
        </a>
        <button onClick={onClose} className="text-sm text-neutral-500 hover:text-neutral-700">
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: '70vh' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-neutral-900 text-white rounded-t-lg">
        <div className="flex items-center gap-2 text-sm font-medium">
          <VideoCameraIcon className="h-4 w-4 text-green-400" />
          Telehealth session
        </div>
        <div className="flex items-center gap-3">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-neutral-300 hover:text-white flex items-center gap-1"
            title="Open in new tab"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            Pop out
          </a>
          <button
            onClick={onClose}
            className="text-neutral-300 hover:text-white"
            title="End session"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* iframe */}
      <div className="relative flex-1 bg-neutral-800 rounded-b-lg overflow-hidden">
        {!iframeLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </div>
        )}
        <iframe
          src={url}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          title="Telehealth video call"
          className="w-full h-full border-0"
          onLoad={() => setIframeLoaded(true)}
          onError={() => setUseExternalFallback(true)}
        />
      </div>
    </div>
  );
}
